import React, { useContext, useEffect, useId, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { isValid, parseISO, setHours, setMinutes, startOfDay } from 'date-fns';
import { AuthContext } from '../context/auth-context';
import { FieldtimeDialog, FormField, useNotify } from '../ui';
import { bidActions, toBidSnapshot, writeStoredBid } from '../../store/bid';
import BidsAPI from '../../services/api/bids/bids-api';
import ProjectsAPI from '../../services/api/admin/projects-api';
import { BID_STATUSES } from '../../utils/bidStatus';
import { usePreference } from '../../Hooks/usePreference';
import Logger from '../../utils/logger';
import { useBidDivisions } from './useBids';

const MAX_PROPOSAL_DIGITS = 10;
const MAX_NAME = 255;
const MAX_BIDTRACER = 100;
const INVALID_DUE = 'Enter a valid due date and time.';

const divisionLabel = (division) => `${division.code} ${division.name}`;
const projectLabel = (project) => `${project.number} — ${project.name}`;

const emptyForm = (divisionId = '') => ({
  divisionId,
  proposalNumber: '',
  name: '',
  status: 'estimating',
  dueDate: null,
  dueTime: null,
  project: null,
  bidtracerId: '',
});

const formFromBid = (bid) => {
  const due = bid.bidDueAt ? parseISO(bid.bidDueAt) : null;
  return {
    divisionId: bid.division?.id ?? '',
    proposalNumber: bid.proposalNumber ?? '',
    name: bid.name ?? '',
    status: bid.status ?? 'estimating',
    dueDate: due,
    dueTime: due,
    project: bid.project ?? null,
    bidtracerId: bid.bidtracerId ?? '',
  };
};

/** Date + optional time -> ISO string; a time without a date is ignored. */
const toBidDueAt = (dueDate, dueTime) => {
  if (!dueDate) return null;
  let due = startOfDay(dueDate);
  if (dueTime) {
    due = setMinutes(setHours(due, dueTime.getHours()), dueTime.getMinutes());
  }
  return due.toISOString();
};

/**
 * BidFormDialog — New bid and Edit bid (ticket #295, plan §4.10).
 *
 * Props:
 *   open     {bool}
 *   onClose  {func}
 *   mode     'create' (default) | 'edit'
 *   bid      {object} the full bid being edited (edit mode)
 *   onSaved  {func}   (bid) => void after a successful save
 */
const BidFormDialog = ({ open, onClose, mode = 'create', bid, onSaved }) => {
  const isEdit = mode === 'edit';
  const { ft } = useTheme().palette;
  const auth = useContext(AuthContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notify = useNotify();
  const selectedBid = useSelector((state) => state.bid.selectedBid);
  const { divisions } = useBidDivisions();
  const [lastDivisionId, setLastDivisionId] = usePreference(
    'bids.lastDivision',
    null
  );

  const [form, setForm] = useState(emptyForm);
  const [invalidDue, setInvalidDue] = useState({ date: false, time: false });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const baseId = useId();
  const dueDateId = `${baseId}-due-date`;
  const dueTimeId = `${baseId}-due-time`;

  // Fill the form each time the dialog opens: the bid for edit, or a blank
  // form on the division used last for create (if it's still active).
  useEffect(() => {
    if (!open) return;
    const lastIsActive = divisions.some((d) => d.id === lastDivisionId);
    setForm(
      isEdit && bid
        ? formFromBid(bid)
        : emptyForm(lastIsActive ? lastDivisionId : '')
    );
    setInvalidDue({ date: false, time: false });
    setFieldErrors({});
    setIsSaving(false);
    // Divisions arriving later must not wipe what the user has typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, bid]);

  // Divisions load once per session; pick the remembered one when they arrive.
  useEffect(() => {
    if (!open || isEdit || form.divisionId !== '') return;
    if (divisions.some((d) => d.id === lastDivisionId)) {
      setForm((prev) => ({ ...prev, divisionId: lastDivisionId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisions]);

  // Linked project options (edit only): the active projects, plus the bid's
  // current project in case it is no longer active.
  useEffect(() => {
    if (!open || !isEdit || !auth.token) return undefined;
    let cancelled = false;
    setProjectsLoading(true);
    ProjectsAPI.getActive(auth.token)
      .then((data) => {
        if (!cancelled) setProjects(data || []);
      })
      .catch((err) => {
        if (!cancelled)
          Logger.error('Failed to load projects for bid link', err);
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isEdit, auth.token]);

  const projectOptions = useMemo(() => {
    const linked = isEdit ? bid?.project : null;
    if (!linked || projects.some((p) => p.id === linked.id)) return projects;
    return [linked, ...projects];
  }, [projects, isEdit, bid]);

  const division = divisions.find((d) => d.id === form.divisionId);
  const bidNumber =
    division && form.proposalNumber
      ? `${division.code}-${form.proposalNumber}`
      : '';

  const isValidForm =
    form.divisionId !== '' &&
    /^\d{1,10}$/.test(form.proposalNumber) &&
    form.name.trim().length > 0 &&
    form.name.trim().length <= MAX_NAME &&
    !invalidDue.date &&
    !invalidDue.time;

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Pickers report half-typed input as an Invalid Date: keep the last good
  // value and flag the field instead of saving something wrong.
  const handleDueChange = (field, key) => (value) => {
    if (value === null || isValid(value)) {
      setInvalidDue((prev) => ({ ...prev, [key]: false }));
      update(field, value);
    } else {
      setInvalidDue((prev) => ({ ...prev, [key]: true }));
    }
  };

  const handleClose = (event, reason) => {
    if (isSaving) return;
    onClose?.(event, reason);
  };

  const refreshSelectedBid = (saved) => {
    const snapshot = toBidSnapshot(saved);
    dispatch(bidActions.selectBid(snapshot));
    writeStoredBid(auth.userId, snapshot);
  };

  const handleSave = async () => {
    if (!isValidForm || isSaving) return;
    setIsSaving(true);
    setFieldErrors({});

    const payload = {
      divisionId: form.divisionId,
      proposalNumber: form.proposalNumber,
      name: form.name.trim(),
      bidDueAt: toBidDueAt(form.dueDate, form.dueTime),
      bidtracerId: form.bidtracerId.trim() || null,
    };
    if (isEdit) {
      payload.status = form.status;
      payload.projectId = form.project?.id ?? null;
    }

    try {
      const saved = isEdit
        ? await BidsAPI.update(bid.id, payload, auth.token)
        : await BidsAPI.create(payload, auth.token);

      if (isEdit) {
        if (selectedBid?.id === saved.id) refreshSelectedBid(saved);
        notify.success('Bid saved');
      } else {
        setLastDivisionId(saved.division?.id ?? form.divisionId);
        refreshSelectedBid(saved);
        navigate('/bids/overview');
        notify.success(`Bid ${saved.bidNumber} created`);
      }
      setIsSaving(false);
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setIsSaving(false);
      const data = err.response?.data;
      if (data?.field) {
        setFieldErrors({ [data.field]: data.message });
      } else {
        Logger.error('Failed to save bid', err);
        notify.error(data?.message || 'The bid could not be saved. Try again.');
      }
    }
  };

  const divisionField = (
    <FormField label="Division" required error={fieldErrors.divisionId}>
      <Select
        size="small"
        fullWidth
        displayEmpty
        value={form.divisionId}
        disabled={isSaving}
        onChange={(e) => update('divisionId', e.target.value)}
        renderValue={(value) => {
          const selected = divisions.find((d) => d.id === value);
          return selected ? (
            divisionLabel(selected)
          ) : (
            <Box component="span" sx={{ color: ft.neutrals.textDisabled }}>
              Choose a division
            </Box>
          );
        }}
      >
        {divisions.map((d) => (
          <MenuItem key={d.id} value={d.id}>
            {divisionLabel(d)}
          </MenuItem>
        ))}
      </Select>
    </FormField>
  );

  const proposalField = (
    <FormField
      label={isEdit ? 'Proposal #' : 'Proposal number'}
      required
      error={fieldErrors.proposalNumber}
    >
      <TextField
        size="small"
        fullWidth
        value={form.proposalNumber}
        disabled={isSaving}
        onChange={(e) =>
          update(
            'proposalNumber',
            e.target.value.replace(/\D/g, '').slice(0, MAX_PROPOSAL_DIGITS)
          )
        }
        inputProps={{
          inputMode: 'numeric',
          maxLength: MAX_PROPOSAL_DIGITS,
          style: { fontVariantNumeric: 'tabular-nums' },
        }}
      />
    </FormField>
  );

  const nameField = (
    <FormField label="Bid name" required error={fieldErrors.name}>
      <TextField
        size="small"
        fullWidth
        value={form.name}
        disabled={isSaving}
        placeholder="Riverside Data Center Phase 2"
        onChange={(e) => update('name', e.target.value)}
        inputProps={{ maxLength: MAX_NAME }}
      />
    </FormField>
  );

  const dueDateField = (
    <FormField
      label="Bid due date"
      error={invalidDue.date ? INVALID_DUE : fieldErrors.bidDueAt}
    >
      <DatePicker
        id={dueDateId}
        value={form.dueDate}
        onChange={handleDueChange('dueDate', 'date')}
        inputFormat="MM/dd/yyyy"
        disabled={isSaving}
        renderInput={(params) => (
          <TextField
            {...params}
            id={dueDateId}
            size="small"
            fullWidth
            error={invalidDue.date || !!fieldErrors.bidDueAt}
            inputProps={{ ...params.inputProps, placeholder: 'MM/DD/YYYY' }}
          />
        )}
      />
    </FormField>
  );

  const dueTimeField = (
    <FormField
      label="Bid due time"
      error={invalidDue.time ? INVALID_DUE : undefined}
    >
      <TimePicker
        id={dueTimeId}
        value={form.dueTime}
        onChange={handleDueChange('dueTime', 'time')}
        inputFormat="h:mm a"
        disableMaskedInput
        disabled={isSaving}
        renderInput={(params) => (
          <TextField
            {...params}
            id={dueTimeId}
            size="small"
            fullWidth
            error={invalidDue.time}
            inputProps={{ ...params.inputProps, placeholder: '2:00 PM' }}
          />
        )}
      />
    </FormField>
  );

  const bidtracerField = (
    <FormField
      label="BidTracer ID"
      helperText={
        isEdit
          ? undefined
          : 'Optional. Links this bid to its BidTracer opportunity.'
      }
      error={fieldErrors.bidtracerId}
    >
      <TextField
        size="small"
        fullWidth
        value={form.bidtracerId}
        disabled={isSaving}
        onChange={(e) => update('bidtracerId', e.target.value)}
        inputProps={{ maxLength: MAX_BIDTRACER }}
      />
    </FormField>
  );

  const saveButton = (
    <Button
      variant="contained"
      onClick={handleSave}
      disabled={!isValidForm || isSaving}
      startIcon={
        isSaving ? <CircularProgress size={16} color="inherit" /> : null
      }
    >
      {isEdit ? 'Save changes' : 'Create bid'}
    </Button>
  );

  const grid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 2,
  };

  return (
    <FieldtimeDialog
      open={open}
      onClose={handleClose}
      size={isEdit ? 'md' : 'sm'}
      title={isEdit ? 'Edit bid' : 'New bid'}
      subtitle={
        isEdit && bid
          ? `${bid.bidNumber} — ${bid.name}`
          : 'Use the proposal number you pulled from the Estimate Log'
      }
      footerCaption={
        isEdit ? undefined : "You'll switch to the new bid once it's created."
      }
      actions={
        <>
          <Button
            variant="text"
            onClick={(e) => handleClose(e, 'cancel')}
            disabled={isSaving}
            sx={{ color: ft.neutrals.textBody }}
          >
            Cancel
          </Button>
          {saveButton}
        </>
      }
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          {isEdit ? (
            <>
              {nameField}
              <Box sx={grid}>
                <Box sx={grid}>
                  {divisionField}
                  {proposalField}
                </Box>
                <FormField label="Status" required error={fieldErrors.status}>
                  <Select
                    size="small"
                    fullWidth
                    value={form.status}
                    disabled={isSaving}
                    onChange={(e) => update('status', e.target.value)}
                  >
                    {BID_STATUSES.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormField>
              </Box>
              <Box sx={grid}>
                {dueDateField}
                {dueTimeField}
              </Box>
              <FormField
                label="Linked project"
                helperText="Optional. The project this bid became once it's won."
                error={fieldErrors.projectId}
              >
                <Autocomplete
                  size="small"
                  fullWidth
                  options={projectOptions}
                  loading={projectsLoading}
                  value={form.project}
                  disabled={isSaving}
                  onChange={(e, value) => update('project', value)}
                  getOptionLabel={projectLabel}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Choose a project" />
                  )}
                />
              </FormField>
              <Box sx={grid}>{bidtracerField}</Box>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={grid}>
                  {divisionField}
                  {proposalField}
                </Box>
                {bidNumber && (
                  <Typography
                    variant="caption"
                    sx={{ color: ft.neutrals.textSecondary }}
                  >
                    Bid number{' '}
                    <Box
                      component="strong"
                      sx={{
                        color: ft.neutrals.textPrimary,
                        fontWeight: ft.fontWeights.medium,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {bidNumber}
                    </Box>
                  </Typography>
                )}
              </Box>
              {nameField}
              <Box sx={grid}>
                {dueDateField}
                {dueTimeField}
              </Box>
              {bidtracerField}
            </>
          )}
        </Box>
      </LocalizationProvider>
    </FieldtimeDialog>
  );
};

export default BidFormDialog;
